import { PDFCheckSVG } from "@components/svgs/pdf-check";
import { PDFCrossSVG } from "@components/svgs/pdf-cross";
import { useAnalysisPDFStyles } from "@hooks/useAnalysisPDFStyle";
import {
  MAAnalysisDataDTO,
  SelectedSubProductDTO,
} from "@models/managed-accounts/entities/step/fee";
import { ManagedAccountFieldType } from "@models/managed-accounts/enums/fieldType";
import { ManagedAccountFieldTypeValue } from "@models/managed-accounts/enums/fieldTypeValue";
import {
  FeatureAnalysisRowDTO,
  SelectedProductDTO,
} from "@models/platform-analysis/entities/steps/feature";
import { ProductRecommendationDTO } from "@models/platform-analysis/entities/steps/summary";
import { FeatureAnalysisRowLevel } from "@models/platform-analysis/enums/feature/featureAnalysisRowLevel";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { useTheme } from "@mui/material";
import { RenderNumericFeesValue } from "@pages/managed-accounts/_id/steps/fee-analysis/components/tables/table-cell";
import { shouldHideSubProductName } from "@pages/platform-analysis-v2/_id/utils";
import { Image, Text, View } from "@react-pdf/renderer";
import { isEmpty, isEqual, isNil } from "lodash";
import { RecommendationLabel } from "../../export-pdf/components/tables/recommendation-label";
import { PDFTable } from "../../export-pdf/components/tables/table";
import { PDFTableBody } from "../../export-pdf/components/tables/table-body";
import { PDFTableCell } from "../../export-pdf/components/tables/table-cell";
import {
  PDFTableRow,
  PDFTableRowType,
} from "../../export-pdf/components/tables/table-row";
import { PDFTitle } from "../../export-pdf/components/title";
import { PDFStyleConfigs } from "../../export-pdf/configs";
import {
  checkContainsMultipleLinesRecommendationBadge,
  generateFeatureSectionIconUrl,
  shouldShowRecommendationLabel,
} from "../../export-pdf/utils";

export const PDFSMAPortfolio = ({
  data,
  selectedSubProducts,
  selectedProducts,
  isAppending = false,
  tableViewMode,
  tablePerformanceScope,
  isFollowingTable,
  countItemTitle,
  numOfHiddenProducts,
  isReview,
  productRecommendations,
  title = "Fee analysis",
}: {
  data?: FeatureAnalysisRowDTO[];
  selectedSubProducts: SelectedSubProductDTO[];
  selectedProducts: SelectedProductDTO[];
  isAppending?: boolean;
  tableViewMode?: FeesDisplayStyle;
  tablePerformanceScope?: FeesPerformanceScope;
  isFollowingTable?: boolean;
  countItemTitle?: string;
  numOfHiddenProducts?: number;
  isReview?: boolean;
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
  title?: string;
}) => {
  const { typographyStyle } = useAnalysisPDFStyles();
  const { palette } = useTheme();
  const shouldExcludePerformance =
    tablePerformanceScope === FeesPerformanceScope.Excluding;
  let shouldShowRedStarInformation = false;

  const tableSelectedProductIds = selectedSubProducts?.map(
    (sp) => sp.productId,
  );
  const tableSelectedProducts = selectedProducts?.filter((p) =>
    tableSelectedProductIds?.includes(p.id),
  );
  const containsMultipleLinesBadge =
    checkContainsMultipleLinesRecommendationBadge({
      selectedProducts: tableSelectedProducts?.map((p) => ({
        ...p,
        recommendation: productRecommendations
          ? productRecommendations[p.id]
          : {},
      })),
      productRecommendations,
    });

  const renderImage = (
    isSubSectionType: boolean,
    row: FeatureAnalysisRowDTO,
  ) => {
    if (!isSubSectionType && !isNil(row?.iconUrl)) {
      const src = generateFeatureSectionIconUrl(row?.iconUrl);

      return <Image style={{ width: 10, height: 10 }} src={src} />;
    }
  };

  const renderRedStarIcon = (analysisData: MAAnalysisDataDTO) => {
    if (!analysisData.tooltip) return;

    const { header, data, additionalText } = analysisData.tooltip;

    if (header || data || additionalText) {
      shouldShowRedStarInformation = true;

      return (
        <Text
          style={{
            ...typographyStyle.body2,
            color: palette.error.main,
            paddingTop: 2,
            fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
          }}
        >
          *
        </Text>
      );
    }
  };

  const renderAnalysisDataValue = (
    variableName: string,
    analysisData: MAAnalysisDataDTO,
    fieldTypeId?: string,
  ) => {
    if (analysisData?.value === ManagedAccountFieldTypeValue.NotDisclosed) {
      const isCashInvestmentFee = isEqual(variableName, "CM_fee");

      return (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              textAlign: "center",
              fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
            }}
          >
            {analysisData.value}
          </Text>

          {isCashInvestmentFee && renderRedStarIcon(analysisData)}
        </View>
      );
    }

    switch (fieldTypeId) {
      case ManagedAccountFieldType.Currency: {
        const isMultipleWithTooltip = [
          analysisData?.isMultiple,
          !isEmpty(analysisData?.tooltip),
          isEqual(analysisData?.value, ManagedAccountFieldType.Multiple),
        ].every(Boolean);

        return (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Text
              style={{
                ...typographyStyle.body2,
                color: palette.text.primary,
                fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
              }}
            >
              {isMultipleWithTooltip ? (
                <>Multiple</>
              ) : (
                <RenderNumericFeesValue
                  alwaysShowDecimal
                  value={analysisData?.value}
                  valuePercentage={analysisData?.valueInPercent}
                  tableViewMode={tableViewMode}
                />
              )}
            </Text>

            {renderRedStarIcon(analysisData) && <></>}
          </View>
        );
      }

      case ManagedAccountFieldType.YesNo:
        switch (analysisData?.value) {
          case ManagedAccountFieldTypeValue.True:
            if (variableName === "Model_inception") {
              return (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Text
                    style={{
                      ...typographyStyle.body2,
                      color: palette.text.primary,
                      fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                    }}
                  >
                    Yes
                  </Text>
                  {renderRedStarIcon(analysisData)}
                </View>
              );
            }

            return <PDFCheckSVG />;
          case ManagedAccountFieldTypeValue.False:
            if (variableName === "Model_inception") {
              return (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Text
                    style={{
                      ...typographyStyle.body2,
                      color: palette.text.primary,
                      fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                    }}
                  >
                    No
                  </Text>
                  <PDFCheckSVG />
                </View>
              );
            }

            return <PDFCrossSVG />;
          case ManagedAccountFieldType.Multiple:
            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                  }}
                >
                  Multiple
                </Text>
                {renderRedStarIcon(analysisData)}
              </View>
            );
          default:
            return (
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                }}
              >
                {analysisData?.value}
              </Text>
            );
        }

      case ManagedAccountFieldType.YesNoText:
        switch (analysisData?.value) {
          case ManagedAccountFieldTypeValue.True:
            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <PDFCheckSVG />
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                  }}
                >
                  Net
                </Text>
              </View>
            );
          case ManagedAccountFieldTypeValue.False:
            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <PDFCrossSVG />
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                  }}
                >
                  Gross
                </Text>
              </View>
            );
          case ManagedAccountFieldType.Multiple:
            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                  }}
                >
                  Multiple
                </Text>
                {renderRedStarIcon(analysisData)}
              </View>
            );
          default:
            return (
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                }}
              >
                {analysisData?.value}
              </Text>
            );
        }
      case ManagedAccountFieldType.DateTime:
        if (analysisData?.value === ManagedAccountFieldTypeValue.Multiple) {
          return (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                }}
              >
                Multiple
              </Text>
              {renderRedStarIcon(analysisData)}
            </View>
          );
        }

        return (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
            }}
          >
            {analysisData?.value}
          </Text>
        );
      case ManagedAccountFieldType.Percentage:
        if (analysisData?.isMultiple && analysisData.tooltip) {
          return (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                }}
              >
                {analysisData.value === ManagedAccountFieldTypeValue.Multiple
                  ? ManagedAccountFieldTypeValue.Multiple
                  : `${analysisData?.value}%`}
              </Text>
              {renderRedStarIcon(analysisData)}
            </View>
          );
        }

        return (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
            }}
          >
            <RenderNumericFeesValue
              valuePercentage={analysisData?.value}
              tableViewMode={FeesDisplayStyle.Percentage}
            />
          </Text>
        );
      case ManagedAccountFieldType.Warning:
        if (analysisData?.value === ManagedAccountFieldTypeValue.NoBreach) {
          return <PDFCheckSVG />;
        }

        if (analysisData?.value === "-") {
          return (
            <Text
              style={{
                ...typographyStyle.body2,
                color: palette.text.primary,
                fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
              }}
            >
              {analysisData?.value}
            </Text>
          );
        }

        return (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Text
              style={{
                ...typographyStyle.body2,
                color: palette.text.primary,
                fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
              }}
            >
              Breach
            </Text>
            {renderRedStarIcon(analysisData)}
          </View>
        );
      case ManagedAccountFieldType.Dropdown:
        if (analysisData?.value === ManagedAccountFieldTypeValue.Multiple) {
          return (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                }}
              >
                {analysisData?.value}
              </Text>
              {renderRedStarIcon(analysisData)}
            </View>
          );
        }

        return (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
            }}
          >
            {analysisData?.value}
          </Text>
        );
      case ManagedAccountFieldType.Text:
        if (analysisData?.isMultiple && analysisData.tooltip) {
          return (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                }}
              >
                Multiple
              </Text>
              {renderRedStarIcon(analysisData)}
            </View>
          );
        }

        return (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
            }}
          >
            {analysisData?.value}
          </Text>
        );
      default:
        return (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontSize: PDFStyleConfigs.SMAPortfolioDefaultFontSize,
            }}
          >
            {analysisData?.value}
          </Text>
        );
    }
  };

  const getTableTitle = () => {
    return isFollowingTable
      ? "SMA portfolio fees by platform (continued)"
      : "SMA portfolio fees by platform";
  };

  const renderTableHeader = () => {
    const tableTitle = getTableTitle();

    if (isAppending) {
      return (
        <View style={{ height: PDFStyleConfigs.TitleHeight }}>
          <Text
            style={{
              ...typographyStyle.subtitle,
              color: palette.common.black,
            }}
          >
            {tableTitle}
          </Text>
        </View>
      );
    }

    return (
      <>
        <PDFTitle title={`${title} (continued)`} subTitle={tableTitle} />
        {selectedSubProducts?.length > 0 && (
          <View style={{ height: 20, marginTop: -1 }}>
            <Text
              style={{
                ...typographyStyle.body1,
                color: palette.common.black,
              }}
            >
              {countItemTitle}
            </Text>
          </View>
        )}
      </>
    );
  };

  const calculateLinesForText = (text: string | undefined) => {
    if (!text?.trim()) return 0;

    const words = text.trim().split(" ");
    const spaceLen = 1;
    let lines = 1;
    let currentLineLen = 0;

    for (const word of words) {
      const wordLen = word.length;
      if (
        wordLen <= PDFStyleConfigs.SMAPortfolioDefaultMaxCharsPerLineOfValue
      ) {
        if (currentLineLen === 0) {
          currentLineLen = wordLen;
          continue;
        }

        const appendedNextWordLen = currentLineLen + spaceLen + wordLen;

        if (
          appendedNextWordLen <=
          PDFStyleConfigs.SMAPortfolioDefaultMaxCharsPerLineOfValue
        ) {
          currentLineLen = appendedNextWordLen;
          continue;
        }

        lines += 1;
        currentLineLen = wordLen;
      } else {
        lines += Math.floor(
          wordLen / PDFStyleConfigs.SMAPortfolioDefaultMaxCharsPerLineOfValue,
        );
        const remainder =
          wordLen % PDFStyleConfigs.SMAPortfolioDefaultMaxCharsPerLineOfValue;

        currentLineLen = remainder > 0 ? remainder : 0;
      }
    }

    return lines;
  };

  const getTableHeaderRowType = (): PDFTableRowType => {
    const numOfLines = selectedSubProducts.map(
      (sp) =>
        calculateLinesForText(sp.productName) + calculateLinesForText(sp.name),
    );

    const maxLines = Math.max(...numOfLines, 0);

    const lineTypeMap: Record<number, PDFTableRowType> = {
      3: "HeaderTripleLineNoLogo",
      4: "HeaderQuadrupleLineNoLogo",
      5: "HeaderQuintupleLineNoLogo",
      6: "HeaderSextupleLineNoLogo",
    };

    return lineTypeMap[maxLines] ?? "HeaderDoubleLineNoLogo";
  };

  const breakLongWord = (word: string | undefined) => {
    if (!word) return "";

    return word
      .split(" ")
      .map((word) =>
        word.length > PDFStyleConfigs.SMAPortfolioDefaultMaxCharsPerLineOfValue
          ? word.replace(
              new RegExp(
                `(.{${PDFStyleConfigs.SMAPortfolioDefaultMaxCharsPerLineOfValue}})`,
                "g",
              ),
              "$1 ",
            )
          : word,
      )
      .join(" ");
  };

  return (
    <>
      {renderTableHeader()}

      {selectedSubProducts?.length === 0 && (
        <View style={{ gap: "5px", marginTop: 15, marginBottom: 15 }}>
          <Text
            style={{
              ...typographyStyle.body1,
              fontWeight: "bold",
              color: "#b1b6bf",
            }}
          >
            No data
          </Text>
          <Text style={{ ...typographyStyle.body1, color: "#b1b6bf" }}>
            No products match the criteria in your filter
          </Text>
        </View>
      )}

      {selectedSubProducts?.length > 0 && (
        <>
          <PDFTable
            tableWidth={
              PDFStyleConfigs.SMAPortfolioFirstCellWidth +
              selectedSubProducts?.length *
                PDFStyleConfigs.SMAPortfolioFollowingCellWidth
            }
          >
            <PDFTableRow
              type={getTableHeaderRowType()}
              additionHeight={
                shouldShowRecommendationLabel(productRecommendations)
                  ? PDFStyleConfigs.TableRowRecommendationHeight +
                    (containsMultipleLinesBadge
                      ? PDFStyleConfigs.TableRowRecommendationHeight +
                        PDFStyleConfigs.TableRowRecommendationHeight
                      : 0)
                  : 0
              }
              forceShowBorderBottom
            >
              <PDFTableCell width={PDFStyleConfigs.SMAPortfolioFirstCellWidth}>
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  Total fees and costs{" "}
                  {tablePerformanceScope === FeesPerformanceScope.Including
                    ? "including"
                    : "excluding"}
                  {"\n"}
                  performance fees (p.a.)
                </Text>
              </PDFTableCell>

              {selectedSubProducts?.map((subProduct, index) => {
                const hideSubProductName = shouldHideSubProductName(
                  subProduct.setupType,
                  subProduct.productName,
                );
                const product = tableSelectedProducts.find(
                  (product) => product.id === subProduct.productId,
                );

                return (
                  <PDFTableCell
                    width={PDFStyleConfigs.SMAPortfolioFollowingCellWidth}
                    key={index}
                    paddingHorizontal={0}
                    paddingVertical={0}
                  >
                    {subProduct.isProductMain && (
                      <Text
                        style={{
                          ...typographyStyle.body2,
                          alignItems: "center",
                          borderRadius: 10,
                          borderWidth: 0.5,
                          borderColor: "#FFAC8A",
                          backgroundColor: "#FFE3D8",
                          fontWeight: "bold",
                          color: "#E84D1E",
                          position: "absolute",
                          top: 1,
                          right: 1,
                          fontSize: 5,
                          paddingTop: 2,
                          paddingRight: 1,
                          paddingLeft: 2,
                        }}
                      >
                        {isReview ? "Current" : "Main"}
                      </Text>
                    )}
                    <View
                      style={{
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <View
                        style={{
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 3,
                          paddingVertical: 5,
                          paddingHorizontal: 2,
                          flexGrow: 1,
                        }}
                      >
                        <Text
                          style={{
                            ...typographyStyle.body2,
                            color: palette.text.primary,
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          {breakLongWord(subProduct.productName)}
                        </Text>

                        {!hideSubProductName && (
                          <Text
                            style={{
                              ...typographyStyle.body2,
                              color: palette.text.primary,
                              fontWeight: "bold",
                              textAlign: "center",
                            }}
                          >
                            {breakLongWord(subProduct.name)}
                          </Text>
                        )}
                      </View>

                      <RecommendationLabel
                        product={product}
                        productRecommendations={productRecommendations}
                        shouldIncreaseHeight={containsMultipleLinesBadge}
                      />
                    </View>
                  </PDFTableCell>
                );
              })}
            </PDFTableRow>

            <PDFTableBody>
              {data?.map((row, rowIndex) => {
                const { level } = row;

                const isSubSectionType =
                  level === FeatureAnalysisRowLevel.SubSection;
                const isFeatureType = level === FeatureAnalysisRowLevel.Feature;
                const colorRowDetailMode = isSubSectionType
                  ? "gray"
                  : isFeatureType
                  ? "white"
                  : "dark";

                return (
                  <PDFTableRow key={rowIndex} color={colorRowDetailMode}>
                    <PDFTableCell
                      alignItems="flex-start"
                      width={PDFStyleConfigs.SMAPortfolioFirstCellWidth}
                    >
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 5,
                        }}
                      >
                        {!isSubSectionType &&
                          renderImage(isSubSectionType, row)}
                        <Text
                          style={{
                            ...typographyStyle.body2,
                            color: palette.text.primary,
                            fontWeight: isFeatureType ? "normal" : "bold",
                            fontSize:
                              PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                          }}
                        >
                          {row?.name}
                        </Text>
                      </View>
                    </PDFTableCell>
                    {row.MAAnalysisData?.map((data, dataIndex) => {
                      return (
                        <PDFTableCell
                          key={dataIndex}
                          width={PDFStyleConfigs.SMAPortfolioFollowingCellWidth}
                          paddingVertical={0}
                        >
                          {isFeatureType ? (
                            renderAnalysisDataValue(
                              row.variableName ?? "",
                              data,
                              data?.fieldTypeId,
                            )
                          ) : (
                            <Text
                              style={{
                                ...typographyStyle.body2,
                                color: palette.text.primary,
                                fontWeight:
                                  isSubSectionType || isFeatureType
                                    ? "normal"
                                    : "bold",
                                fontSize:
                                  PDFStyleConfigs.SMAPortfolioDefaultFontSize,
                              }}
                            >
                              <RenderNumericFeesValue
                                value={data?.value}
                                valuePercentage={data?.valueInPercent}
                                valueExcPerf={data?.valueExcPerf}
                                valuePercentageExcPerf={
                                  data?.valueExcPerfInPercent
                                }
                                tableViewMode={tableViewMode}
                                shouldExcludePerformance={
                                  shouldExcludePerformance
                                }
                              />
                            </Text>
                          )}
                        </PDFTableCell>
                      );
                    })}
                  </PDFTableRow>
                );
              })}
            </PDFTableBody>
          </PDFTable>

          {shouldShowRedStarInformation && (
            <View style={{ marginTop: 5 }}>
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.error.main,
                }}
              >
                * See web version of this report for full details
              </Text>
            </View>
          )}
        </>
      )}

      {!isNil(numOfHiddenProducts) && (
        <View style={{ marginTop: 10 }}>
          <Text
            style={{
              ...typographyStyle.body2,
              color: "#1E1970",
              fontWeight: "bold",
            }}
          >
            {`${numOfHiddenProducts} ${
              numOfHiddenProducts === 1 ? "product" : "products"
            } hidden`}
          </Text>
        </View>
      )}
    </>
  );
};
